import type { DrawingModuleType } from "../DrawingSingleton";
import type { DrawingModule } from "../types/DrawingWeb";
import { deleteObj } from "./delete";

/**
 * Return the BlockTableRecord ID of the currently active space.
 * Resolves to model space when in model space, or the paper space BTR
 * when a paper space layout is active.
 */
export function getCurrentSpaceId(
  db: DrawingModule.OdDbDatabase,
): DrawingModule.OdDbObjectId {
  return db.currentSpaceId();
}

/**
 * Open an ODA object by ID and cast to a specific type.
 * Properly cleans up the intermediate object to avoid memory leaks.
 */
export function openAs<T>(
  id: DrawingModule.OdDbObjectId,
  type: { cast(obj: DrawingModule.OdRxObject): T },
  openMode: DrawingModule.OpenMode,
  debug = false,
): T {
  let pObj: DrawingModule.OdDbObject | null = null;
  try {
    if (debug) console.log("safe");
    pObj = id.safeOpenObject(openMode, true);
    if (debug) console.log("cast");
    const instance = type.cast(pObj);
    if (debug) console.log("return");
    return instance;
  } catch {
    throw new Error(`openAs : can't open id`);
  } finally {
    if (pObj) {
      if (debug) console.log("delete");
      deleteObj(pObj);
    }
  }
}

/**
 * Get the handle string from an entity ID.
 *
 * Handles are persistent identifiers that survive entity modifications,
 * unlike object IDs which may change after erase/unerase operations.
 *
 * @param entityId The entity's object ID
 * @returns The handle string (hex format), or empty string on error
 */
export function getEntityHandle(entityId: DrawingModule.OdDbObjectId): string {
  const handle = entityId.getHandle();
  const handleStr = handle.ascii();
  const result = handleStr.toString();
  deleteObj(handleStr);
  deleteObj(handle);
  return result;
}

/**
 * Find an entity by its database handle string.
 *
 * Handles are persistent identifiers that survive entity modifications,
 * unlike object IDs which may change after erase/unerase operations.
 *
 * @param module The ODA drawing module
 * @param db The database to search in
 * @param handle The handle string (hex format)
 * @returns The entity's object ID, or null if not found
 */
export function findEntityByHandle(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase | null,
  handle: string,
): DrawingModule.OdDbObjectId | null {
  if (!db) return null;

  try {
    const handleStr = new module.OdString(handle);
    const handleObj = module.OdDbHandle.ctr2(handleStr);
    const entityId = db.getOdDbObjectId(handleObj, false, 0);
    deleteObj(handleStr);
    deleteObj(handleObj);
    if (entityId.isNull() || entityId.isErased() || !entityId.isValid()) {
      //console.log(entityId.isNull(), entityId.isErased(), entityId.isValid())
      deleteObj(entityId);
      return null;
    }
    return entityId;
  } catch {
    return null;
  }
}
